import { type ApiSchemas, call } from '@rental/contracts/client'
import { STAFF_LOCALES, type StaffLocale } from '@rental/domain'
import { format } from '@rental/i18n'
import { Button, Field, Notice } from '@rental/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { ChevronLeft } from 'lucide-react'
import { type FormEvent, useId, useState } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { api } from '../api'
import { ChoiceChips } from '../components/ChoiceChips'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { OWNER_CODE, OwnerCodeField } from '../components/OwnerCodeField'
import { PageHeader } from '../components/PageHeader'
import { ReasonList } from '../components/SubPanel'
import { Guard, LoadState } from '../components/states'
import { FIELD, FIELD_ROW, ProblemNotice, StaffInput } from '../components/ui'
import { noImplicitSubmit } from '../forms'
import { useLocale } from '../i18n'
import { LOOKS_LIKE_EMAIL } from '../people/people'
import { loadPeople, PEOPLE_KEY } from '../people/queries'
import { ShownOnce } from '../people/ShownOnce'
import { problemOf, type StaffProblem } from '../problems'

/** S9 (ADM-1, Q-51): a new account, for the holders of users.manage. */
export function NewPersonScreen() {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  return (
    <Guard allowed={can(access, 'users.manage')} title={m.newPerson.title}>
      <NewPerson />
    </Guard>
  )
}

/**
 * The name, the e-mail, the role and the e-mail language, approved with the
 * Owner's code. The answer is the account and its one-time password (and,
 * for a role that signs in with a code, its key), shown once on this page:
 * held in this screen's state alone, never in the app's cache, and gone with
 * the page. The request takes no Idempotency-Key, whose store would keep the
 * password: an answer lost on the way is an account that exists, which the
 * next try is refused as (email_taken), and its page gives a new password.
 */
function NewPerson() {
  const { messages: m } = useLocale()
  const copy = m.newPerson
  const { takeOver } = useAccess()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const roles = useQuery({ queryKey: PEOPLE_KEY, queryFn: loadPeople })
  const reasonsId = useId()
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<string | null>(null)
  const [locale, setLocale] = useState<StaffLocale>('it')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<StaffProblem | null>(null)
  const [created, setCreated] = useState<ApiSchemas['PersonCreated'] | null>(null)

  const back = (
    <Link to="/people" className="flex min-h-touch items-center gap-1 self-start">
      <ChevronLeft aria-hidden="true" className="size-4" />
      {copy.back}
    </Link>
  )

  if (created) {
    return (
      <>
        <PageHeader back={back} title={copy.title} />
        <OutcomeNotice>
          <p>{format(copy.done, { name: created.person.displayName })}</p>
        </OutcomeNotice>
        <ShownOnce
          name={created.person.displayName}
          password={created.password}
          authenticator={created.authenticator}
          onDone={() =>
            void navigate({ to: '/people/$userId', params: { userId: created.person.id } })
          }
        />
      </>
    )
  }

  const roleOptions = roles.data?.roles ?? []
  const codeRole = roleOptions.find((option) => option.code === role)?.requiresCode === true
  const reasons = busy
    ? []
    : [
        ...(displayName.trim() === '' ? [copy.why.name] : []),
        ...(LOOKS_LIKE_EMAIL.test(email.trim()) ? [] : [copy.why.email]),
        ...(role ? [] : [copy.why.role]),
        ...(OWNER_CODE.test(code) ? [] : [copy.why.code]),
      ]

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (busy || reasons.length > 0 || !role) return
    setProblem(null)
    setBusy(true)
    const result = await call(
      api.POST('/v1/staff/people', {
        body: { displayName: displayName.trim(), email: email.trim(), role, locale, code },
      }),
    )
    setBusy(false)
    // A code is spent whether or not the account was made: a new one is typed either way.
    setCode('')
    if (result.ok) {
      setCreated(result.data)
      void queryClient.invalidateQueries({ queryKey: PEOPLE_KEY })
      return
    }
    if (takeOver(result.problem)) return
    setProblem(result.problem)
  }

  return (
    <>
      <PageHeader back={back} title={copy.title} intro={copy.intro} />
      <LoadState data={roles.data} problem={problemOf(roles.error)}>
        {() => (
          <form
            onSubmit={submit}
            onKeyDown={noImplicitSubmit}
            noValidate
            className="glass-pane flex max-w-2xl flex-col gap-4 rounded-card p-4 sm:p-6"
          >
            <div className={FIELD_ROW}>
              <Field className={FIELD} label={copy.name} hint={copy.nameHint} required>
                {(control) => (
                  <StaffInput
                    {...control}
                    autoComplete="off"
                    maxLength={60}
                    value={displayName}
                    onChange={(event) => setDisplayName(event.target.value)}
                  />
                )}
              </Field>
              <Field className={FIELD} label={copy.email} hint={copy.emailHint} required>
                {(control) => (
                  <StaffInput
                    {...control}
                    type="email"
                    autoComplete="off"
                    maxLength={254}
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                )}
              </Field>
            </div>
            <ChoiceChips
              name="new-person-role"
              label={copy.role}
              options={roleOptions.map((option) => ({
                value: option.code,
                label: (m.roles as Readonly<Record<string, string>>)[option.code] ?? option.code,
              }))}
              value={role}
              onChange={setRole}
            />
            {codeRole ? (
              <Notice tone="info">
                <p>{copy.codeRole}</p>
              </Notice>
            ) : null}
            <ChoiceChips
              name="new-person-locale"
              label={copy.locale}
              options={STAFF_LOCALES.map((value) => ({ value, label: m.languages[value] }))}
              value={locale}
              onChange={setLocale}
            />
            <p className="text-xs text-text-muted">{copy.localeHint}</p>
            <OwnerCodeField className={FIELD} label={copy.code} value={code} onChange={setCode} />
            <ProblemNotice problem={problem} />
            <div>
              <Button
                type="submit"
                variant="staff"
                busy={busy}
                disabled={reasons.length > 0}
                aria-describedby={reasons.length > 0 ? reasonsId : undefined}
              >
                {copy.submit}
              </Button>
            </div>
            <ReasonList id={reasonsId} reasons={reasons} />
          </form>
        )}
      </LoadState>
    </>
  )
}
